import { useMemo, useCallback, useRef, useEffect, useState } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  useReactFlow,
  useStore,
  useOnViewportChange,
  type Node,
  type NodeMouseHandler,
} from "@xyflow/react";
import "@xyflow/react/dist/base.css";
import "./diagram-view.css";
import type { CoordTransform } from "@diagram/draw-overlay";
import type { DiagramRendererProps } from "./types";
import { useAutoLayout } from "./useAutoLayout";
import { nodeSizeMapFingerprint, type NodeSizeMap } from "./spec-to-elk";
import { ElkEdge } from "./ElkEdge";
import { DefaultNode } from "./DefaultNode";
import { GroupNode } from "./GroupNode";
import {
  AppearanceProvider,
  resolveEffectiveTheme,
  useAppearanceTheme,
} from "./appearance-context";
import { SketchArrowMarkerDefs } from "./sketch/SketchArrowMarker";

/**
 * A node that renders bigger than the size ELK laid out for it overlaps its
 * neighbours, so even a 1px growth is worth another layout pass. A node that
 * renders smaller only leaves the layout slightly loose, so it takes a
 * substantial shrink to be worth re-running ELK. The asymmetry is what keeps
 * a running simulation — where metric labels change width every frame — from
 * re-laying out the whole graph continuously.
 */
const GROW_TOLERANCE = 1;
const SHRINK_TOLERANCE = 16;

/** How many recent measure -> layout transitions to keep for cycle detection. */
const SIZE_HISTORY_LENGTH = 6;

/**
 * Record a measure -> layout handover, or refuse it.
 *
 * Returns the updated history, or `null` when this exact `from -> to`
 * handover has already been made — the signature of an oscillating
 * measure/layout loop, which is the only way this feedback can fail to
 * settle (node sizes are content-driven, so there is no runaway to cap).
 *
 * Keying on the transition rather than on the destination alone is what
 * makes this safe. The DOM legitimately returns to a size map it held
 * earlier — resetting a simulation restores the idle rendering — and
 * refusing that would strand the layout describing sizes nothing on screen
 * has any more: loose gaps and detached edges when the stale map is the
 * larger one, overlapping nodes when it is the smaller one.
 */
export function recordSizeTransition(
  history: readonly string[],
  fromFingerprint: string,
  toFingerprint: string,
): string[] | null {
  const key = `${fromFingerprint}->${toFingerprint}`;
  if (history.includes(key)) return null;
  const next = [...history, key];
  return next.length > SIZE_HISTORY_LENGTH
    ? next.slice(next.length - SIZE_HISTORY_LENGTH)
    : next;
}

/**
 * Compare measured node dimensions against the sizes last fed to ELK.
 * Returns the new sizes when any node has outgrown (or substantially
 * undershot) what the layout assumed, otherwise undefined.
 *
 * Group nodes are excluded: their dimensions are computed by ELK from their
 * children rather than by content, so React Flow only ever reports back the
 * size we just gave them. Including them added a re-layout pass per group
 * that changed nothing (specToElk ignores group entries anyway).
 */
export function extractChangedSizes(
  flowNodes: Node[],
  prevSizes: NodeSizeMap | undefined,
): NodeSizeMap | undefined {
  const sizes: NodeSizeMap = {};
  let anyDifferent = false;

  for (const n of flowNodes) {
    if (n.type === "group") continue;
    const w = n.measured?.width;
    const h = n.measured?.height;
    if (w != null && h != null) {
      sizes[n.id] = { width: w, height: h };
      const prev = prevSizes?.[n.id];
      if (
        !prev ||
        w - prev.width > GROW_TOLERANCE ||
        h - prev.height > GROW_TOLERANCE ||
        prev.width - w > SHRINK_TOLERANCE ||
        prev.height - h > SHRINK_TOLERANCE
      ) {
        anyDifferent = true;
      }
    }
  }

  return anyDifferent && Object.keys(sizes).length > 0 ? sizes : undefined;
}

/**
 * Build a fingerprint string from React Flow's measured node sizes.
 * Used to detect content-driven size changes after initial convergence.
 */
export function measuredSizesFingerprint(
  nodes: readonly {
    id: string;
    measured?: { width?: number; height?: number };
  }[],
): string {
  let fp = "";
  for (const n of nodes) {
    const m = n.measured;
    if (m != null && m.width != null && m.height != null) {
      fp += `${n.id}:${String(Math.round(m.width))}x${String(Math.round(m.height))},`;
    }
  }
  return fp;
}

const BUILTIN_NODE_TYPES = {
  default: DefaultNode,
  group: GroupNode,
};

const BUILTIN_EDGE_TYPES = {
  elk: ElkEdge,
};

/**
 * Inner component that uses the ReactFlow hooks (requires ReactFlowProvider).
 */
function DiagramInner({
  spec,
  nodeTypes: consumerNodeTypes,
  onNodeClick,
  onNodeDrag,
  layoutOptions,
  className,
  renderOverlay,
  colorMode,
}: DiagramRendererProps) {
  const theme = useAppearanceTheme();
  const [measuredSizes, setMeasuredSizes] = useState<NodeSizeMap | undefined>();
  const { nodes, edges, layoutReady } = useAutoLayout(
    spec,
    layoutOptions,
    measuredSizes,
  );
  const {
    getViewport,
    setViewport,
    screenToFlowPosition,
    flowToScreenPosition,
  } = useReactFlow();
  const containerRef = useRef<HTMLDivElement>(null);
  const prevWidthRef = useRef<number | null>(null);

  // Subscribers registered through CoordTransform.subscribe — fired on every
  // pan/zoom so an attached overlay can re-render against the new viewport
  // without React itself re-rendering DiagramInner. A Set lets a subscriber
  // deregister cheaply via the returned unsubscribe.
  const viewportSubsRef = useRef(new Set<() => void>());
  useOnViewportChange({
    onChange: useCallback(() => {
      for (const cb of viewportSubsRef.current) cb();
    }, []),
  });

  // Rebuilt only when the React Flow projection helpers change identity
  // (effectively once per provider lifetime). Memoising keeps the
  // CoordTransform reference stable across renders so overlay components
  // can use it as an effect dependency without infinite loops.
  const transform = useMemo<CoordTransform>(
    () => ({
      toContent(clientX, clientY) {
        const p = screenToFlowPosition({ x: clientX, y: clientY });
        return { x: p.x, y: p.y };
      },
      toScreen(x, y) {
        // React Flow returns viewport-pixel coords. The overlay SVG is
        // positioned at `inset: 0` of the container, so its local origin
        // is `containerRef.current.getBoundingClientRect().{left,top}`.
        // Subtract that offset so the caller can use the result as
        // SVG-local coordinates (path `d`, `<g transform="translate(...)">`).
        const p = flowToScreenPosition({ x, y });
        const host = containerRef.current;
        // Defensive null-guard: the ref attaches before any DOM event
        // (and thus before the consumer ever calls toScreen). Excluded
        // from coverage because it's not realistically reachable.
        /* v8 ignore next */
        if (!host) return { left: p.x, top: p.y };
        const r = host.getBoundingClientRect();
        return { left: p.x - r.left, top: p.y - r.top };
      },
      subscribe(cb) {
        viewportSubsRef.current.add(cb);
        return () => {
          viewportSubsRef.current.delete(cb);
        };
      },
    }),
    [screenToFlowPosition, flowToScreenPosition],
  );

  // Track structural fingerprint to know when to re-measure
  const prevStructureRef = useRef("");
  // The measure -> layout handovers most recently made, used by
  // recordSizeTransition to spot an oscillating feedback loop.
  const sizeTransitionsRef = useRef<string[]>([]);

  // Subscribe to React Flow's internal node measurement state.
  // We read from nodeLookup (internal nodes) rather than s.nodes because
  // RF only sets `measured` on internal nodes — propagating to s.nodes
  // requires onNodesChange, which we don't use (layout is ELK-driven).
  const nodesInitialized = useStore((s) => {
    if (s.nodeLookup.size === 0) return false;
    for (const n of s.nodeLookup.values()) {
      const m = n.measured;
      if (m.width == null || m.height == null) return false;
    }
    return true;
  });

  // Build a fingerprint of all measured sizes so we detect content-driven
  // size changes (e.g. a table gaining rows) even after initial convergence.
  // The CSS override (width/height: max-content !important) makes wrappers
  // auto-size to content, so node.measured reflects intrinsic dimensions.
  // Read internal nodes with measured dimensions for size extraction.
  // NOTE: This selector creates a new array every call, so useStore will
  // re-render on every store update. This is intentional — the derived
  // measuredSizesFp fingerprint is the actual change-detection mechanism.
  const internalNodes: Node[] = useStore((s) => {
    const result: Node[] = [];
    for (const n of s.nodeLookup.values()) {
      result.push(n);
    }
    return result;
  });

  const measuredSizesFp = measuredSizesFingerprint(internalNodes);

  // When nodes are measured, extract sizes and feed back to layout.
  // Also re-runs when measuredSizesFp changes (content resized).
  // The effect body is covered by the browser spike; jsdom cannot
  // exercise React Flow's internal ResizeObserver measurement.
  // A pass budget used to guard this instead, but the several ResizeObserver
  // ticks that land per layout spent it before the sizes converged, and once
  // spent nothing reopened it while the spec sat still — the diagram stayed
  // frozen with nodes laid out on top of each other. recordSizeTransition
  // only refuses a handover it has already made, so convergence is never cut
  // short. Its logic is unit-tested; only this React plumbing is excluded,
  // since jsdom cannot drive React Flow's ResizeObserver measurement.
  useEffect(() => {
    /* v8 ignore next -- guarded by layoutReady render gate */
    if (!nodesInitialized || !layoutReady) return;
    /* v8 ignore start -- needs real DOM measurement */
    const changed = extractChangedSizes(internalNodes, measuredSizes);
    if (!changed) return;
    const nextTransitions = recordSizeTransition(
      sizeTransitionsRef.current,
      nodeSizeMapFingerprint(measuredSizes),
      nodeSizeMapFingerprint(changed),
    );
    if (!nextTransitions) return;
    sizeTransitionsRef.current = nextTransitions;
    setMeasuredSizes(changed);
    /* v8 ignore stop */
  }, [
    nodesInitialized,
    layoutReady,
    internalNodes,
    measuredSizes,
    measuredSizesFp,
  ]);

  // Forget the transition history when the structure changes: handovers
  // recorded for a different set of nodes say nothing about this one. We
  // deliberately keep the existing measuredSizes: clearing them would force
  // every node back to the default 160×40 and cause a jarring intermediate
  // layout. Existing sizes remain valid and new nodes simply use defaults
  // until the next measurement pass picks them up.
  useEffect(() => {
    const fp =
      JSON.stringify(spec.nodes.map((n) => n.id).sort()) +
      JSON.stringify(spec.groups.map((g) => g.id).sort());
    if (fp !== prevStructureRef.current) {
      prevStructureRef.current = fp;
      sizeTransitionsRef.current = [];
    }
  }, [spec]);

  // Adjust viewport to maintain the same center point when the container resizes
  // (e.g. editor collapse/expand). This preserves user pan/zoom instead of resetting.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !layoutReady) return;
    prevWidthRef.current = el.clientWidth;
    const observer = new ResizeObserver((entries) => {
      const newWidth = entries[0].contentRect.width;
      const oldWidth = prevWidthRef.current;
      prevWidthRef.current = newWidth;
      if (oldWidth === null || oldWidth === newWidth) return;
      requestAnimationFrame(() => {
        const vp = getViewport();
        void setViewport({
          x: vp.x + (newWidth - oldWidth) / 2,
          y: vp.y,
          zoom: vp.zoom,
        });
      });
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [layoutReady, getViewport, setViewport]);

  const nodeTypes = useMemo(
    () => ({
      ...BUILTIN_NODE_TYPES,
      ...consumerNodeTypes,
    }),
    [consumerNodeTypes],
  );

  const handleNodeClick: NodeMouseHandler = useCallback(
    (_, node) => {
      onNodeClick?.(node.id);
    },
    [onNodeClick],
  );

  const handleNodeDragStop = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      onNodeDrag?.(node.id, node.position);
    },
    [onNodeDrag],
  );

  if (!layoutReady) {
    return (
      <div
        className={className}
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#888",
        }}
        data-testid="diagram-loading"
      >
        Computing layout...
      </div>
    );
  }

  const overlayNode = renderOverlay?.(transform);

  // Only add `position: relative` when an overlay is mounted so the
  // unchanged-DOM invariant for the default (no-overlay) consumer holds
  // byte-for-byte. The overlay host is `position: absolute; inset: 0` and
  // needs the container as its positioning ancestor.
  const containerStyle: React.CSSProperties =
    overlayNode != null
      ? { width: "100%", height: "100%", position: "relative" }
      : { width: "100%", height: "100%" };

  return (
    <div
      ref={containerRef}
      style={containerStyle}
      // Hand-drawn mode tags the renderer root so diagram-view.css can scope
      // the handwritten font to diagram content. `undefined` in default mode
      // renders no class attribute at all — the DOM stays byte-identical,
      // protecting existing visual regression baselines.
      className={theme === "hand-drawn" ? "diagram-hand-drawn" : undefined}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={BUILTIN_EDGE_TYPES}
        onNodeClick={handleNodeClick}
        onNodeDragStop={handleNodeDragStop}
        fitView
        className={className}
        colorMode={colorMode}
        proOptions={{ hideAttribution: true }}
      >
        <Background />
        <Controls />
        {/* Shared sketchy arrowhead def, mounted only in hand-drawn mode so
            the default DOM stays byte-identical (protects visual baselines). */}
        {theme === "hand-drawn" ? <SketchArrowMarkerDefs /> : null}
      </ReactFlow>
      {/* Overlay host. Only mounted when a renderOverlay was supplied so
          that the default DOM tree is byte-identical to the pre-overlay
          version (visual regression baselines stay stable). The wrapper
          itself is pointer-events: none; the overlay opts in to events on
          its own children so clicks fall through when drawing is off. */}
      {overlayNode != null ? (
        <div className="diagram-view-overlay-host">{overlayNode}</div>
      ) : null}
    </div>
  );
}

/**
 * Top-level diagram renderer component. Accepts a library-agnostic
 * DiagramSpec and renders an interactive, automatically laid-out
 * diagram using React Flow and ELKjs.
 *
 * Wraps the inner component in ReactFlowProvider so hooks work.
 * Consumer can supply custom nodeTypes to override default rendering.
 */
export function DiagramRenderer(props: DiagramRendererProps) {
  // Effective theme precedence: viewer override (prop) > model-authored
  // spec appearance > default. Distributed via context so node/edge
  // components can branch on it without churning node data references.
  const theme = resolveEffectiveTheme(props.appearance, props.spec.appearance);
  return (
    <ReactFlowProvider>
      <AppearanceProvider theme={theme}>
        <DiagramInner {...props} />
      </AppearanceProvider>
    </ReactFlowProvider>
  );
}
